import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import {
  ALL_GAMES,
  ALL_AVATARS,
  nextMilestone,
  playCountSchema,
  recentPlaysSchema,
  skillLevelsSchema,
  type PlayCount,
  type RecentPlays,
  type SkillLevels,
  type AvatarKey,
} from '@hazel/shared'

import { api, ApiError } from '../api/client.js'
import { useScaleChoice } from '../layouts/scale-context.js'
import { useSound } from '../sound/sound-context.js'
import { useSession } from '../auth/session-context.js'
import { Avatar } from '../components/Avatar.js'
import { Button } from '../components/Button.js'
import { Card } from '../components/Card.js'
import { Input } from '../components/Input.js'
import { Tally } from '../components/Tally.js'
import { RecentRounds } from './RecentRounds.js'
import { WhereSheIsUpTo } from './WhereSheIsUpTo.js'

export function Profile() {
  const { user, refresh } = useSession()
  const navigate = useNavigate()
  const size = useScaleChoice()
  const sound = useSound()

  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [played, setPlayed] = useState<PlayCount | null>(null)
  const [recent, setRecent] = useState<RecentPlays | null>(null)
  const [levels, setLevels] = useState<SkillLevels | null>(null)

  useEffect(() => {
    void api<unknown>('/api/plays/count')
      .then((body) => {
        // Parsed, not assumed. A page that trusts a response's shape is a page
        // that crashes when the shape changes, which is exactly what happened
        // here — and is the same lesson the socket payloads already taught.
        const parsed = playCountSchema.safeParse(body)
        if (parsed.success) setPlayed(parsed.data)
      })
      // Not being able to count is not worth interrupting anybody over.
      .catch(() => undefined)

    void api<unknown>('/api/plays')
      .then((body) => {
        const parsed = recentPlaysSchema.safeParse(body)
        if (parsed.success) setRecent(parsed.data)
      })
      .catch(() => undefined)

    void api<unknown>('/api/maths/levels')
      .then((body) => {
        // Parsed against a schema like everything else here. Hand-rolled
        // typeof checks are how a page ends up half-trusting a shape, which is
        // the bug the other two calls on this screen exist to avoid.
        const parsed = skillLevelsSchema.safeParse(body)
        if (parsed.success) setLevels(parsed.data)
      })
      .catch(() => undefined)
  }, [])

  const nextUp = played ? nextMilestone(played.total) : null

  if (!user) return null

  async function update(body: Record<string, unknown>, done: string): Promise<void> {
    setError(null)
    setMessage(null)

    try {
      await api(`/api/users/${user!.id}`, { method: 'PATCH', body: JSON.stringify(body) })
      await refresh()
      setMessage(done)
    } catch (caught: unknown) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.')
    }
  }

  async function logout(): Promise<void> {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined)
    await refresh()
    await navigate('/login')
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-4">
        <Avatar teddy={user.avatarKey} name={user.displayName} size="large" />
        <div>
          <h1 className="text-(length:--text-hero)">{user.displayName}</h1>
          <p className="text-ink-muted">
            {user.username} · {user.role === 'parent' ? 'grown-up' : 'player'}
          </p>
        </div>
      </div>

      <Card>
        <h2 className="text-(length:--text-heading) mb-3">What you&rsquo;ve been playing</h2>

        {/* Counts of what she has done, not marks out of ten. There are no
            scores and no bests here, deliberately — and a count of games is the
            one number nobody can inflate without doing the thing it measures. */}
        {/* Named, so it is one thing rather than a loose row of numbers — the
            teddy names appear again further down in the avatar picker. */}
        <div className="flex flex-wrap gap-3" role="group" aria-label="Games played">
          <Tally label="games played" value={played?.total ?? 0} tone="teal" />
          {ALL_GAMES.filter((game) => (played?.byGame?.[game.key] ?? 0) > 0).map((game) => (
            <Tally key={game.key} label={game.teddy} value={played?.byGame?.[game.key] ?? 0} />
          ))}
        </div>

        {played && played.total > 0 ? (
          nextUp ? (
            <p className="text-ink-muted text-(length:--text-small) mt-3">
              {`${nextUp - played.total} more to reach ${nextUp}.`}
            </p>
          ) : null
        ) : (
          <p className="text-ink-muted text-(length:--text-small) mt-3">
            This fills up once you start playing.
          </p>
        )}
      </Card>

      {recent ? <RecentRounds rounds={recent.rounds} daysPlayed={recent.daysPlayed} /> : null}

      {levels ? <WhereSheIsUpTo levels={levels} /> : null}

      <Card>
        <h2 className="text-(length:--text-heading) mb-3">What you&rsquo;re called</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            const wanted = name.trim()
            if (wanted === '') return

            void update({ displayName: wanted }, 'Name changed.').then(() => setName(''))
          }}
          className="flex flex-col gap-3"
        >
          <Input
            label="Your name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={user.displayName}
            autoComplete="nickname"
            // The same ceiling the server keeps, so a long name is stopped
            // where she is typing it rather than after a round trip.
            maxLength={64}
            hint="What you are called on the games and in a room. Not how you log in."
          />
          <Button type="submit" disabled={name.trim() === ''}>
            Change my name
          </Button>
        </form>
      </Card>

      {/*
        Which size the screen shows at, kept in this browser rather than
        against the account.

        It is a fact about the screen this is being read on, not about the
        person: the same account on the television wants the big scale and on
        the tablet does not, so storing it on the server would have one of
        those two always wrong.

        This is also the only way back from the television scale now. There
        used to be an undo button in the header, which meant it sat in the
        chrome of every screen on the display it is most often showing on.
      */}
      {size ? (
        <Card>
          <h2 className="text-(length:--text-heading) mb-3">Size on this screen</h2>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Screen size">
            {(
              [
                { key: 'default', label: 'Normal', hint: 'A phone, a tablet or a laptop.' },
                { key: 'tv', label: 'TV', hint: 'Read from across a room.' },
              ] as const
            ).map((choice) => (
              <button
                key={choice.key}
                type="button"
                onClick={() => {
                  size.choose(choice.key)
                }}
                aria-pressed={size.scale === choice.key}
                className={[
                  'flex min-h-(--size-tap) flex-col items-start gap-1 rounded-(--radius-control)',
                  'border-border border-2 px-4 py-2 text-left',
                  size.scale === choice.key ? 'bg-surface-sunken ring-primary ring-2' : '',
                ].join(' ')}
              >
                <span>{choice.label}</span>
                <span className="text-ink-muted text-(length:--text-small)">{choice.hint}</span>
              </button>
            ))}
          </div>
          <p className="text-ink-muted text-(length:--text-small) mt-3">
            Remembered on this device only.
          </p>
        </Card>
      ) : null}

      <Card>
        <h2 className="text-(length:--text-heading) mb-3">Sound</h2>

        <button
          type="button"
          onClick={() => {
            sound.setOn(!sound.on)
          }}
          aria-pressed={sound.on}
          className={[
            'flex min-h-(--size-tap) w-full items-center justify-between gap-3',
            'rounded-(--radius-control) border-border border-2 px-4 py-2 text-left',
            sound.on ? 'bg-surface-sunken ring-primary ring-2' : '',
          ].join(' ')}
        >
          <span>Music and sounds</span>
          <span className="text-ink-muted text-(length:--text-small)">
            {sound.on ? 'On' : 'Off'}
          </span>
        </button>

        {/*
          Says where it applies, because on this page it very likely does not.
          A switch that appears to do nothing is worse than no switch, and this
          one is most often looked at on a phone — which never makes a sound.
        */}
        <p className="text-ink-muted text-(length:--text-small) mt-3">
          {sound.heard
            ? 'Remembered on this device only. Press M on the game menu to switch it off from the sofa.'
            : 'Only the television plays sound, so this changes nothing here — it is remembered on this device only, and the television has a switch of its own.'}
        </p>
      </Card>

      <Card>
        <h2 className="text-(length:--text-heading) mb-3">Your picture</h2>
        <div className="flex flex-wrap gap-2">
          {ALL_AVATARS.map((teddy) => (
            <button
              key={teddy.key}
              type="button"
              onClick={() =>
                void update({ avatarKey: teddy.key satisfies AvatarKey }, 'Picture changed.')
              }
              aria-pressed={user.avatarKey === teddy.key}
              className={[
                'flex min-h-(--size-tap) flex-col items-center gap-1 rounded-(--radius-control) p-2',
                user.avatarKey === teddy.key ? 'bg-surface-sunken ring-primary ring-2' : '',
              ].join(' ')}
            >
              <Avatar teddy={teddy.key} name={teddy.name} size="small" />
              <span className="text-(length:--text-small)">{teddy.name}</span>
            </button>
          ))}
        </div>
      </Card>

      <Card>
        <h2 className="text-(length:--text-heading) mb-3">Change your password</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            void update({ password }, 'Password changed. Other devices are logged out.').then(() =>
              setPassword(''),
            )
          }}
          className="flex flex-col gap-3"
        >
          <Input
            label="New password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            hint="At least 8 characters. Other devices will be logged out."
            required
          />
          <Button type="submit">Change it</Button>
        </form>
      </Card>

      {message ? (
        <p role="status" className="text-teal">
          {message}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-coral">
          {error}
        </p>
      ) : null}

      <Button variant="secondary" onClick={() => void logout()}>
        Log out
      </Button>
    </div>
  )
}
